import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CustomPropTypes from 'CustomPropTypes';
import { Table } from 'antd';
import { Link } from 'react-router-dom';
import ForkliftMaintenanceRecordAddForm from './forklift-maintenance-record-add-form';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMaintenanceRecordsByForkliftId, fetchMaintenanceRecordsDetails } from './store/slice';
import { selectForkliftMaintenanceRecordsData, selectMaintenanceRecordData } from './store/selectors';
import { format } from 'date-fns';

const initData = {
  workOrderNumber: '',
  date: '',
  description: '',
  maintenanceType: '1',
  personnelName: '',
  downTime: '',
  pictures: '',
  serviceRecords: [],
  personnelRecords: [
    {
      personnelName: '',
      startTime: new Date().toISOString().slice(0, 16),
      endTime: new Date().toISOString().slice(0, 16),
    },
  ],
};

const formatDate = date => {
  if (!date) return '';
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch (err) {
    console.error('Invalid date:', date);
    return '';
  }
};

const ForkliftMaintenanceHistory = ({ data, onCloseModal, forkliftId }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [showMaintenanceHistory, setshowMaintenanceHistory] = useState(true);
  const [viewingRecord, setViewingRecord] = useState(null);

  // Debug logging
  console.log('Forklift Maintenance History - forkliftId:', forkliftId);

  const forkliftMaintenanceRecordsData = useSelector(selectForkliftMaintenanceRecordsData);
  const maintenanceRecordData = useSelector(selectMaintenanceRecordData);

  const maintenanceTypeOptions = [
    { value: 1, label: 'Preventative/Predictive' },
    { value: 2, label: 'Forklift Down' },
    { value: 3, label: 'Emergency' },
    { value: 4, label: 'Scheduled' },
  ];

  useEffect(() => {
    if (forkliftId) {
      dispatch(fetchMaintenanceRecordsByForkliftId(forkliftId));
    }
  }, [forkliftId]);

  useEffect(() => {
    if (viewingRecord && maintenanceRecordData?.workOrderNumber) {
      setFormData(maintenanceRecordData);
      setshowMaintenanceHistory(false);
    }
  }, [viewingRecord, maintenanceRecordData]);

  const handleViewClick = record => {
    setViewingRecord(record);
    dispatch(fetchMaintenanceRecordsDetails(record.id));
  };

  const handleMaintenanceRecord = () => {
    setFormData(initData); // reset form to empty
    setViewingRecord(null); // no record being viewed
    setshowMaintenanceHistory(!showMaintenanceHistory);
  };

  const columns = [
    { title: 'Work Order Number', dataIndex: 'workOrderNumber', key: 'workOrderNumber' },
    { title: 'Date', dataIndex: 'date', key: 'date', render: date => formatDate(date)},
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { 
      title: 'Preventative/Predictive', 
      dataIndex: 'maintenanceType', 
      key: 'maintenanceType',
      render: (type) => {
        const option = maintenanceTypeOptions.find(opt => opt.value === type);
        return option ? option.label : type;
      }
    },
    { title: 'Personnel Name', dataIndex: 'personnelName', key: 'personnelName' },
    { title: 'Down Time Duration', dataIndex: 'downTime', key: 'downTime' },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'id',
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link className="me-2 p-2" onClick={() => handleViewClick(record)}>
              View
            </Link>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3">
        <button className="btn btn-info me-2 " onClick={() => handleMaintenanceRecord()}>
          {showMaintenanceHistory ? '+ Add Forklift Maintenance Record' : '← Back to Maintenance History'}
        </button>
      </div>
      {showMaintenanceHistory ? (
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12">
            <Table columns={columns} dataSource={forkliftMaintenanceRecordsData } />
          </div>
        </div>
      ) : (
        <ForkliftMaintenanceRecordAddForm
          key={formData?.workOrderNumber || 'new'}
          data={formData}
          forkliftId={forkliftId}
          maintenanceTypeOptions={maintenanceTypeOptions}
          onCloseModal={onCloseModal}
        />
      )}
    </>
  );
};

ForkliftMaintenanceHistory.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.shape(CustomPropTypes.machines),
  onChanges: PropTypes.func,
};

export default ForkliftMaintenanceHistory;
